<template>
  <div class="project-container">
    <!-- Hero Section -->
    <header class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">Projet Universitaire (S5)</div>
        <h1 class="hero-title">Cloud Infrastructure</h1>
        <p class="hero-subtitle">Haute Disponibilité & Plan de Continuité d'Activité (PCA)</p>
        <div class="hero-meta">
          <span class="meta-item"><i class="mdi mdi-calendar"></i> Septembre 2024 - Janvier 2025</span>
          <span class="meta-item"><i class="mdi mdi-account-group"></i> Équipe de 3</span>
          <span class="meta-item"><i class="mdi mdi-server-network"></i> Architecture Multi-Nodes</span>
        </div>
      </div>
      <div class="hero-image">
        <div class="icon-placeholder">
          <i class="mdi mdi-cloud-check"></i>
        </div>
      </div>
    </header>

    <!-- Overview Card -->
    <section class="card overview-card">
      <div class="card-header">
        <i class="mdi mdi-information-outline"></i>
        <h2>Vue d'ensemble</h2>
      </div>
      <div class="card-content">
        <p class="lead-text">
          Ce projet de fin de cycle (Semestre 5) visait à concevoir et déployer une <strong>infrastructure cloud complète</strong>,
          sécurisée et hautement disponible. L'enjeu majeur était la mise en place d'un <strong>Plan de Continuité d'Activité (PCA)</strong>
          permettant de garantir la disponibilité des services même en cas de défaillance matérielle.
        </p>
        <p>
          L'infrastructure héberge une suite de services collaboratifs (Mail, Forge Git, Stockage, Authentification) interconnectés
          via un <strong>SSO centralisé</strong>. Le déploiement a été entièrement automatisé via des outils d'<strong>Infrastructure as Code (IaC)</strong>,
          assurant une reproductibilité parfaite et une gestion simplifiée de la configuration.
        </p>
        <div class="tech-stack">
          <span class="tech-badge">Docker Swarm</span>
          <span class="tech-badge">Terraform</span>
          <span class="tech-badge">Ansible</span>
          <span class="tech-badge">GlusterFS</span>
          <span class="tech-badge">Keycloak</span>
          <span class="tech-badge">Traefik</span>
          <span class="tech-badge">Keepalived</span>
        </div>
      </div>
    </section>

    <!-- PCA & HA Section -->
    <section class="card pca-card">
      <div class="card-header">
        <i class="mdi mdi-shield-airplane"></i>
        <h2>Résilience & Haute Disponibilité</h2>
      </div>
      <div class="card-content">
        <h3 class="section-subtitle">Plan de Continuité d'Activité (PCA)</h3>
        <p>
          La stratégie de résilience repose sur une redondance à tous les niveaux de la pile technologique, permettant
          une survie de l'infrastructure à la perte d'un nœud complet (RPO = 0).
        </p>

        <div class="feature-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-database-sync"></i>
            </div>
            <h4>GlusterFS</h4>
            <p>Système de fichiers distribué assurant la réplication synchrone des données entre les nœuds. 
            Toute modification sur un volume est immédiatement propagée sur le cluster.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-ip-network"></i>
            </div>
            <h4>Keepalived (VRRP)</h4>
            <p>Gestion d'une IP flottante (VIP) partagée entre les nœuds. En cas de panne du nœud maître, 
            l'IP bascule instantanément sur un nœud de secours sans interruption de service.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-transit-connection-variant"></i>
            </div>
            <h4>Docker Swarm</h4>
            <p>Orchestration du cluster gérant le cycle de vie des conteneurs. En cas de crash d'un nœud, 
            les services sont automatiquement replanifiés sur les nœuds sains.</p>
          </div>

          <div class="feature-item">
            <div class="feature-icon">
              <i class="mdi mdi-lan-check"></i>
            </div>
            <h4>Traefik & Failover</h4>
            <p>Reverse proxy dynamique gérant le routage et le health-checking. Intégré avec Cloudflare Tunnels 
            pour un accès externe sécurisé et résilient.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- IAM & Security Section -->
    <section class="card security-card">
      <div class="card-header">
        <i class="mdi mdi-lock-check"></i>
        <h2>Sécurité & Identité (IAM)</h2>
      </div>
      <div class="card-content">
        <h3 class="section-subtitle">Défense en Profondeur</h3>
        <p>
          La sécurité est au cœur de l'architecture, avec une segmentation réseau stricte et un contrôle d'accès
          granulaire via un SSO moderne.
        </p>

        <div class="capability-list">
          <div class="capability-item">
            <i class="mdi mdi-account-key"></i>
            <span><strong>SSO Centralisé :</strong> Keycloak couplé à OpenLDAP pour une gestion unique des identités.</span>
          </div>
          <div class="capability-item">
            <i class="mdi mdi-shield-key"></i>
            <span><strong>Multi-Facteurs (MFA) :</strong> Authentification TOTP obligatoire pour tous les services critiques.</span>
          </div>
          <div class="capability-item">
            <i class="mdi mdi-wall"></i>
            <span><strong>WAF ModSecurity :</strong> Filtrage des attaques applicatives (SQLi, XSS) via Traefik et Nginx.</span>
          </div>
          <div class="capability-item">
            <i class="mdi mdi-certificate"></i>
            <span><strong>TLS 1.3 & PKI :</strong> Chiffrement de bout en bout avec gestion automatisée des certificats.</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Monitoring Section -->
    <section class="card monitoring-card">
      <div class="card-header">
        <i class="mdi mdi-monitor-dashboard"></i>
        <h2>Observabilité & SIEM</h2>
      </div>
      <div class="card-content">
        <p>
          Un système complet de monitoring et d'alerting permet de surveiller la santé de l'infrastructure et de
          détecter les incidents en temps réel.
        </p>

        <div class="image-showcase">
          <div class="stats-grid">
            <div class="stat-item">
              <i class="mdi mdi-chart-bell-curve-cumulative"></i>
              <h4>Prometheus</h4>
              <p>Collecte des métriques</p>
            </div>
            <div class="stat-item">
              <i class="mdi mdi-text-box-search"></i>
              <h4>Loki</h4>
              <p>Agrégation des logs</p>
            </div>
            <div class="stat-item">
              <i class="mdi mdi-view-dashboard"></i>
              <h4>Grafana</h4>
              <p>Dashboards temps réel</p>
            </div>
          </div>
          <span class="image-caption">Stack d'observabilité complète intégrée au cluster</span>
        </div>
      </div>
    </section>

    <!-- IUT Competencies Validation -->
    <section class="card validation-card">
      <div class="card-header">
        <i class="mdi mdi-certificate-outline"></i>
        <h2>Validation des Compétences IUT</h2>
      </div>
      <div class="card-content">
        <div class="validation-grid">
          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">AC 1</span>
              <h4>Administrer : Automatisation</h4>
            </div>
            <p><strong>Pourquoi :</strong> Assurer un déploiement reproductible et sans erreur humaine de l'infrastructure complexe.</p>
            <p><strong>Comment :</strong> Utilisation de <strong>Terraform</strong> pour le provisioning des VMs et <strong>Ansible</strong> pour la configuration automatisée des nœuds du cluster.</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">AC 2</span>
              <h4>Administrer : Configuration Avancée</h4>
            </div>
            <p><strong>Pourquoi :</strong> Maîtriser les services réseaux pour permettre la communication inter-conteneurs sécurisée.</p>
            <p><strong>Comment :</strong> Configuration de réseaux <strong>Docker Overlay</strong>, déploiement de <strong>Traefik</strong> en mode cluster et gestion des entrées DNS via Cloudflare.</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">AC 3</span>
              <h4>Administrer : Sécurité Infrastructure</h4>
            </div>
            <p><strong>Pourquoi :</strong> Protéger les données critiques et les services contre les intrusions et les attaques applicatives.</p>
            <p><strong>Comment :</strong> Implémentation d'un <strong>WAF ModSecurity</strong>, isolation des réseaux (DMZ) et authentification <strong>MFA via Keycloak</strong>.</p>
          </div>

          <div class="validation-item">
            <div class="v-header">
              <span class="v-code">CE3.04</span>
              <h4>Administrer : Continuité d'Activité</h4>
            </div>
            <p><strong>Pourquoi :</strong> Garantir que les services restent disponibles 24/7 même en cas de panne matérielle lourde.</p>
            <p><strong>Comment :</strong> Mise en place du <strong>PCA</strong> avec <strong>GlusterFS</strong> pour la réplication synchrone et <strong>Keepalived</strong> pour le basculement d'IP (failover).</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Learning Outcomes -->
    <section class="card learning-card">
      <div class="card-header">
        <i class="mdi mdi-lightbulb-on"></i>
        <h2>Compétences Développées</h2>
      </div>
      <div class="card-content">
        <div class="learning-grid">
          <div class="learning-category">
            <h4>DevSecOps & IaC</h4>
            <ul>
              <li>Provisioning via Terraform</li>
              <li>Configuration via Ansible</li>
              <li>Pipelines CI/CD sécurisées</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Administration Système</h4>
            <ul>
              <li>Orchestration Docker Swarm</li>
              <li>Cluster GlusterFS</li>
              <li>Gestion réseau overlay</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Sécurité (IAM/IAM)</h4>
            <ul>
              <li>Déploiement SSO Keycloak</li>
              <li>Administration OpenLDAP</li>
              <li>Configuration WAF & TLS</li>
            </ul>
          </div>

          <div class="learning-category">
            <h4>Architecture Logicielle</h4>
            <ul>
              <li>Design de systèmes résilients</li>
              <li>Planification de PCA/PRA</li>
              <li>Segmentation réseau multi-tier</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';

@Component({})
export default class SAEInfraComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'Inter', 'Segoe UI', sans-serif;
  line-height: 1.8;
  color: var(--text-color);
  background-color: var(--background-color);
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  padding-left: 100px;
}

/* Hero Section */
.hero-section {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 40px;
  margin-bottom: 60px;
  padding: 60px 40px;
  background: linear-gradient(135deg, var(--card-background) 0%, var(--dark-bg-secondary) 100%);
  border-radius: 24px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.hero-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.hero-badge {
  display: inline-block;
  width: fit-content;
  padding: 8px 16px;
  background: rgba(19, 124, 139, 0.2);
  color: var(--primary-color);
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: 600;
  margin-bottom: 16px;
  border: 1px solid var(--primary-color);
}

.hero-title {
  font-size: 3.5em;
  font-weight: 800;
  color: var(--h1-color);
  margin: 0 0 16px 0;
  background: linear-gradient(135deg, #ffffff 0%, var(--tertiary-color) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 1.4em;
  color: var(--sub-color);
  margin-bottom: 24px;
  font-weight: 400;
}

.hero-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-muted);
  font-size: 0.95em;
}

.meta-item i {
  color: var(--primary-color);
}

.hero-image {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-placeholder {
  width: 150px;
  height: 150px;
  background: rgba(19, 124, 139, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--primary-color);
  box-shadow: 0 0 30px rgba(19, 124, 139, 0.3);
}

.icon-placeholder i {
  font-size: 80px;
  color: var(--primary-color);
}

/* Card Styles */
.card {
  background: var(--card-background);
  border-radius: 20px;
  padding: 40px;
  margin-bottom: 32px;
  border: 1px solid var(--dark-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(19, 124, 139, 0.2);
  border-color: var(--primary-color);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--dark-border);
}

.card-header i {
  font-size: 32px;
  color: var(--primary-color);
}

.card-header h2 {
  font-size: 2em;
  font-weight: 700;
  color: var(--h2-color);
  margin: 0;
}

.card-content {
  color: var(--text-color);
}

.lead-text {
  font-size: 1.1em;
  line-height: 1.8;
  margin-bottom: 24px;
  color: var(--text-color);
}

.section-subtitle {
  font-size: 1.4em;
  color: var(--h2-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

/* Tech Stack */
.tech-stack {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.tech-badge {
  padding: 10px 20px;
  background: rgba(19, 124, 139, 0.15);
  color: var(--primary-color);
  border-radius: 12px;
  font-size: 0.9em;
  font-weight: 600;
  border: 1px solid rgba(19, 124, 139, 0.3);
  transition: all 0.3s ease;
}

.tech-badge:hover {
  background: rgba(19, 124, 139, 0.25);
  transform: translateY(-2px);
}

/* Feature Grid */
.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin: 32px 0;
}

.feature-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: var(--dark-bg-card);
  border-color: var(--primary-color);
  transform: translateY(-4px);
}

.feature-icon {
  width: 50px;
  height: 50px;
  background: rgba(19, 124, 139, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.feature-icon i {
  font-size: 24px;
  color: var(--primary-color);
}

.feature-item h4 {
  font-size: 1.1em;
  color: var(--h2-color);
  margin: 0 0 8px 0;
  font-weight: 600;
}

.feature-item p {
  color: var(--text-muted);
  margin: 0;
  font-size: 0.95em;
  line-height: 1.6;
}

/* Capability List */
.capability-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 32px 0;
}

.capability-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: var(--dark-bg-secondary);
  border-radius: 12px;
  border-left: 3px solid var(--primary-color);
  transition: all 0.3s ease;
}

.capability-item:hover {
  background: var(--dark-bg-card);
  transform: translateX(8px);
}

.capability-item i {
  font-size: 24px;
  color: var(--primary-color);
}

.capability-item span {
  color: var(--text-color);
  font-size: 1em;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.stat-item {
  padding: 20px;
  background: var(--dark-bg-secondary);
  border-radius: 12px;
  text-align: center;
  border: 1px solid var(--dark-border);
}

.stat-item i {
  font-size: 40px;
  color: var(--primary-color);
  margin-bottom: 12px;
}

.stat-item h4 {
  margin: 0;
  color: var(--h2-color);
}

.stat-item p {
  margin: 8px 0 0 0;
  color: var(--text-muted);
  font-size: 0.9em;
}

/* Validation Card Styles */
.validation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.validation-item {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
  border-top: 4px solid var(--primary-color);
}

.v-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.v-code {
  padding: 4px 10px;
  background: var(--primary-color);
  color: #ffffff;
  border-radius: 6px;
  font-weight: 800;
  font-size: 0.9em;
  white-space: nowrap;
}

.v-header h4 {
  margin: 0;
  color: var(--h2-color);
  font-size: 1.1em;
}

.validation-item p {
  font-size: 0.95em;
  margin: 8px 0;
  line-height: 1.6;
}

.validation-item strong {
  color: var(--primary-color);
}

/* Learning Grid */
.learning-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.learning-category {
  padding: 24px;
  background: var(--dark-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--dark-border);
}

.learning-category h4 {
  font-size: 1.2em;
  color: var(--primary-color);
  margin: 0 0 16px 0;
  font-weight: 600;
}

.learning-category ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.learning-category li {
  padding: 8px 0 8px 24px;
  color: var(--text-color);
  position: relative;
  font-size: 0.95em;
}

.learning-category li::before {
  content: '→';
  position: absolute;
  left: 0;
  color: var(--primary-color);
  font-weight: bold;
}

/* Responsive */
@media (max-width: 768px) {
  .project-container {
    padding-left: 20px;
  }

  .hero-section {
    grid-template-columns: 1fr;
    padding: 30px 20px;
  }

  .hero-title {
    font-size: 2.5em;
  }

  .card {
    padding: 24px;
  }

  .hero-image {
    display: none;
  }
}
</style>